import { useEffect, useState } from "react";
import {
  Clock,
  AlertTriangle,
  XCircle,
  CheckCircle,
  Clock10,
  UserX,
  AlertCircle,
  Shield,
  Timer,
  Plus,
  Calendar,
  Ban
} from "lucide-react";
import "./PresenceAlert.scss";

export default function PresenceAlert({
  application,
  userType,
  onExtendTolerance,
  onReportNoShow,
  onInvalidateApplication,
}) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Validação de dados
  if (!application || !application.job) return null;

  // Parse das datas
  const [year, month, day] = application.job.date.split("T")[0].split("-");
  const [hours, minutes] = application.job.startTime.split(":");

  const startTime = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    0
  );

  // Cálculo dos estados
  const isLate =
    now >= startTime &&
    !application.isOnTheWay &&
    !application.isArrived;

  const toleranceUntilTime = application.toleranceUntil 
    ? new Date(application.toleranceUntil).getTime() 
    : 0;
  const nowTime = now.getTime();

  const toleranceActive =
    application.toleranceExtended &&
    application.toleranceUntil &&
    toleranceUntilTime > nowTime;

  const toleranceExpired =
    application.toleranceExtended &&
    application.toleranceUntil &&
    toleranceUntilTime <= nowTime;

  // Se não está atrasado, não renderiza
  if (!isLate) return null;

  // Formatação de tempo
  const formatTime = (date) => {
    return date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Componente de contagem regressiva
  const CountdownTimer = ({ targetTime }) => {
    const remainingMs = targetTime - nowTime;
    const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
    const min = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
    const sec = String(totalSeconds % 60).padStart(2, "0");

    return (
      <div className="ip-alert__countdown">
        <div className="ip-alert__countdown-label">
          <Clock10 size={14} />
          <span>Prazo restante</span>
        </div>
        <strong>{min}:{sec}</strong>
      </div>
    );
  };

  // CASO A: Tolerância ativa
  if (toleranceActive) {
    return (
      <div className="ip-alert ip-alert--info">
        <div className="ip-alert__icon-wrapper ip-alert__icon-wrapper--info">
          <Clock className="ip-alert__icon" />
        </div>

        <div className="ip-alert__content">
          <div className="ip-alert__header">
            <h4>Tolerância concedida</h4>
            <span className="ip-alert__badge ip-alert__badge--info">
              <Shield size={12} />
              Ativa
            </span>
          </div>

          <p>
            A empresa concedeu uma tolerância adicional para realização do check-in.
          </p>

          <CountdownTimer targetTime={toleranceUntilTime} />

          <p className="ip-alert__deadline">
            O check-in deverá ser realizado até{" "}
            <strong>
              {new Date(application.toleranceUntil).toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </strong>
            . Após esse horário a candidatura poderá ser invalidada automaticamente.
          </p>

          <div className="ip-alert__actions">
            <button
              className="ip-alert__button ip-alert__button--danger"
              onClick={onInvalidateApplication}
            >
              <Ban size={16} />
              Invalidar candidatura
            </button>
          </div>
        </div>
      </div>
    );
  }

  // CASO B: Tolerância expirada
  if (toleranceExpired) {
    return (
      <div className="ip-alert ip-alert--danger">
        <div className="ip-alert__icon-wrapper ip-alert__icon-wrapper--danger">
          <XCircle className="ip-alert__icon" />
        </div>

        <div className="ip-alert__content">
          <div className="ip-alert__header">
            <h4>Prazo de tolerância encerrado</h4>
            <span className="ip-alert__badge ip-alert__badge--danger">
              <AlertCircle size={12} />
              Expirado
            </span>
          </div>

          <p>
            O freelancer não realizou o check-in dentro do período de tolerância concedido.
          </p>

          <p className="ip-alert__highlight">
            Caso ele realmente não tenha comparecido, reporte a falta para reabrir esta vaga como <strong>URGENTE</strong>.
          </p>

          <div className="ip-alert__actions">
            <button
              className="ip-alert__button ip-alert__button--danger ip-alert__button--urgent"
              onClick={onReportNoShow}
            >
              <UserX size={16} />
              Reportar falta e reabrir vaga
            </button>
          </div>
        </div>
      </div>
    );
  }

  // CASO C: Atrasado padrão
  return (
    <div className="ip-alert ip-alert--warning">
      <div className="ip-alert__icon-wrapper ip-alert__icon-wrapper--warning">
        <AlertTriangle className="ip-alert__icon" />
      </div>

      <div className="ip-alert__content">
        <div className="ip-alert__header">
          <h4>Freelancer atrasado</h4>
          <span className="ip-alert__badge ip-alert__badge--warning">
            <Clock size={12} />
            Atraso detectado
          </span>
        </div>

        <p>
          O turno iniciou às{" "}
          <strong>{formatTime(startTime)}</strong>{" "}
          porém ainda não houve confirmação de deslocamento nem de chegada ao local de trabalho.
        </p>

        {userType === "Company" && (
          <div className="ip-alert__actions">
            <button
              className="ip-alert__button ip-alert__button--primary"
              onClick={onExtendTolerance}
            >
              <Plus size={16} />
              Conceder +30 minutos
            </button>

            <button
              className="ip-alert__button ip-alert__button--danger"
              onClick={onInvalidateApplication}
            >
              <Ban size={16} />
              Invalidar candidatura
            </button>
          </div>
        )}

        {userType === "Freelancer" && (
          <div className="ip-alert__info-message">
            <AlertCircle size={16} />
            <span>Entre em contato com a empresa para resolver o atraso.</span>
          </div>
        )}
      </div>
    </div>
  );
}